/**
 * PaginaAdmin - Painel de Administração
 *
 * CONTEXTO DE NEGÓCIO:
 * Dashboard administrativo para gestão completa do sistema:
 * - Estatísticas gerais
 * - Gestão de usuários (listar, ativar/desativar, alterar role, resetar senha)
 * - Gestão de processos (listar, visualizar, excluir)
 * - Visualização de logs
 * - Gestão de arquivos (listar, download, excluir)
 * - Métricas do sistema (Prometheus)
 *
 * ACESSO:
 * Apenas usuários com role='admin' podem acessar esta página.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  FileText,
  Activity,
  HardDrive,
  Settings,
  RefreshCw,
  Trash2,
  Download,
  Shield,
  ShieldOff,
  Key,
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronRight,
  FolderOpen,
  Filter,
  Eye,
  XCircle,
  BarChart3,
  Database,
  Building2,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Crown,
  UserMinus,
  Plus,
} from 'lucide-react';
import {
  obterEstatisticas,
  listarUsuarios,
  alterarRoleUsuario,
  ativarDesativarUsuario,
  resetarSenhaUsuario,
  listarProcessos,
  obterProcessoDetalhe,
  excluirProcesso,
  consultarLogs,
  listarArquivos,
  downloadArquivo,
  excluirArquivo,
  obterMetricasResumo,
  obterMetricasPrometheus,
  iniciarDownloadBlob,
  listarOrganizacoesAdmin,
  listarMembrosOrganizacaoAdmin,
  criarOrganizacaoAdmin,
  atualizarOrganizacaoAdmin,
  excluirOrganizacaoAdmin,
  adicionarMembroOrganizacaoAdmin,
  alterarRoleMembroAdmin,
  removerMembroOrganizacaoAdmin,
  type EstatisticasSistema,
  type UsuarioAdmin,
  type ProcessoAdmin,
  type ProcessoDetalheAdmin,
  type LogEntry,
  type ArquivoInfo,
  type MetricasResumo,
  type OrganizacaoAdmin,
  type MembroOrganizacaoAdmin,
} from '../servicos/servicoAdmin';
import {
  obterResumoConsumoTokens,
  listarConsumoUsuarios,
  listarConsumoOrganizacoes,
  sincronizarOrganizacoesConsumo,
} from '../servicos/servicoConsumoTokens';
import type {
  ResumoConsumoTokens,
  ConsumoTokensUsuario,
  ConsumoTokensOrganizacao,
} from '../tipos/tiposConsumoTokens';
import { formatarTokens, formatarCustoUSD } from '../tipos/tiposConsumoTokens';

// ==================== TIPOS LOCAIS ====================

type TabAtiva = 'visao-geral' | 'usuarios' | 'processos' | 'logs' | 'arquivos' | 'metricas' | 'tokens' | 'organizacoes';

interface Notificacao {
  tipo: 'sucesso' | 'erro' | 'info';
  mensagem: string;
}

// ==================== COMPONENTES AUXILIARES ====================

function CardEstatistica({
  titulo,
  valor,
  icone: Icone,
  cor,
}: {
  titulo: string;
  valor: string | number;
  icone: React.ElementType;
  cor: string;
}) {
  return (
    <div className="rounded-lg bg-white p-6 shadow-md">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{titulo}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{valor}</p>
        </div>
        <div className={`rounded-full p-3 ${cor}`}>
          <Icone className="size-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function Badge({ children, variante = 'default' }: { children: React.ReactNode; variante?: 'success' | 'warning' | 'error' | 'default' | 'info' }) {
  const cores: Record<string, string> = {
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
    default: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cores[variante]}`}>
      {children}
    </span>
  );
}

function NotificacaoBanner({ notificacao, onFechar }: { notificacao: Notificacao; onFechar: () => void }) {
  const estilos: Record<string, string> = {
    sucesso: 'bg-green-50 border-green-200 text-green-800',
    erro: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };
  const icones: Record<string, React.ElementType> = {
    sucesso: CheckCircle,
    erro: AlertCircle,
    info: AlertCircle,
  };
  const Icone = icones[notificacao.tipo];

  return (
    <div className={`mb-4 flex items-center justify-between rounded-lg border p-4 ${estilos[notificacao.tipo]}`}>
      <div className="flex items-center gap-2">
        <Icone className="size-5" />
        <span>{notificacao.mensagem}</span>
      </div>
      <button onClick={onFechar} className="text-current hover:opacity-70">
        <XCircle className="size-5" />
      </button>
    </div>
  );
}

// ==================== COMPONENTE PRINCIPAL ====================

export function PaginaAdmin(): JSX.Element {
  // Estado geral
  const [tabAtiva, setTabAtiva] = useState<TabAtiva>('visao-geral');
  const [carregando, setCarregando] = useState(false);
  const [notificacao, setNotificacao] = useState<Notificacao | null>(null);

  // Estado de dados
  const [estatisticas, setEstatisticas] = useState<EstatisticasSistema | null>(null);
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([]);
  const [processos, setProcessos] = useState<ProcessoAdmin[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [arquivos, setArquivos] = useState<ArquivoInfo[]>([]);
  const [metricas, setMetricas] = useState<MetricasResumo | null>(null);
  const [metricasPrometheus, setMetricasPrometheus] = useState<string>('');

  // Estado de consumo de tokens
  const [resumoTokens, setResumoTokens] = useState<ResumoConsumoTokens | null>(null);
  const [consumoUsuarios, setConsumoUsuarios] = useState<ConsumoTokensUsuario[]>([]);
  const [consumoOrganizacoes, setConsumoOrganizacoes] = useState<ConsumoTokensOrganizacao[]>([]);
  const [filtroTokensPeriodo, setFiltroTokensPeriodo] = useState<string>('30'); // últimos N dias
  const [filtroTokensUsuario, setFiltroTokensUsuario] = useState<string>(''); // filtro de busca por usuário
  const [filtroTokensOrganizacao, setFiltroTokensOrganizacao] = useState<string>(''); // filtro de busca por organização
  const [sincronizandoTokens, setSincronizandoTokens] = useState(false); // estado de sincronização

  // Estado de organizações (admin)
  const [organizacoes, setOrganizacoes] = useState<OrganizacaoAdmin[]>([]);
  const [orgExpandida, setOrgExpandida] = useState<string | null>(null);
  const [membrosOrgExpandida, setMembrosOrgExpandida] = useState<MembroOrganizacaoAdmin[]>([]);
  const [carregandoMembros, setCarregandoMembros] = useState(false);
  const [incluirOrgsInativas, setIncluirOrgsInativas] = useState(false);
  
  // Modais de organização
  const [modalNovaOrg, setModalNovaOrg] = useState(false);
  const [novaOrgNome, setNovaOrgNome] = useState('');
  const [novaOrgDescricao, setNovaOrgDescricao] = useState('');
  const [novaOrgOwnerId, setNovaOrgOwnerId] = useState('');
  
  const [modalAdicionarMembro, setModalAdicionarMembro] = useState<{ aberto: boolean; organizacaoId: string; organizacaoNome: string }>({
    aberto: false,
    organizacaoId: '',
    organizacaoNome: '',
  });
  const [novoMembroId, setNovoMembroId] = useState('');
  const [novoMembroRole, setNovoMembroRole] = useState<'admin' | 'member'>('member');

  // Estado de modais
  const [processoDetalhe, setProcessoDetalhe] = useState<ProcessoDetalheAdmin | null>(null);
  const [modalResetSenha, setModalResetSenha] = useState<{ aberto: boolean; usuarioId: string; usuarioNome: string }>({
    aberto: false,
    usuarioId: '',
    usuarioNome: '',
  });
  const [novaSenha, setNovaSenha] = useState('');

  // Filtros
  const [filtroUsuarioRole, setFiltroUsuarioRole] = useState<string>('');
  const [filtroUsuarioAtivo, setFiltroUsuarioAtivo] = useState<string>('');
  const [filtroLogNivel, setFiltroLogNivel] = useState<string>('');
  const [filtroArquivoDiretorio, setFiltroArquivoDiretorio] = useState<string>('uploads');

  // Funções de notificação
  const mostrarNotificacao = useCallback((tipo: Notificacao['tipo'], mensagem: string) => {
    setNotificacao({ tipo, mensagem });
    setTimeout(() => setNotificacao(null), 5000);
  }, []);

  // ==================== CARREGAMENTO DE DADOS ====================

  const carregarEstatisticas = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await obterEstatisticas();
      setEstatisticas(dados);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [mostrarNotificacao]);

  const carregarUsuarios = useCallback(async () => {
    try {
      setCarregando(true);
      const params: Record<string, string | boolean> = {};
      if (filtroUsuarioRole) params.filtro_role = filtroUsuarioRole;
      if (filtroUsuarioAtivo) params.filtro_ativo = filtroUsuarioAtivo === 'true';
      const dados = await listarUsuarios(params);
      setUsuarios(dados);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [filtroUsuarioRole, filtroUsuarioAtivo, mostrarNotificacao]);

  const carregarProcessos = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await listarProcessos();
      setProcessos(dados);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [mostrarNotificacao]);

  const carregarLogs = useCallback(async () => {
    try {
      setCarregando(true);
      const params: Record<string, string | number> = { limite: 100 };
      if (filtroLogNivel) params.nivel = filtroLogNivel;
      const resposta = await consultarLogs(params);
      setLogs(resposta.logs);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [filtroLogNivel, mostrarNotificacao]);

  const carregarArquivos = useCallback(async () => {
    try {
      setCarregando(true);
      const resposta = await listarArquivos({ diretorio: filtroArquivoDiretorio });
      setArquivos(resposta.arquivos);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [filtroArquivoDiretorio, mostrarNotificacao]);

  const carregarMetricas = useCallback(async () => {
    try {
      setCarregando(true);
      const [resumo, prometheus] = await Promise.all([
        obterMetricasResumo(),
        obterMetricasPrometheus(),
      ]);
      setMetricas(resumo);
      setMetricasPrometheus(prometheus);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [mostrarNotificacao]);

  const carregarConsumoTokens = useCallback(async () => {
    try {
      setCarregando(true);
      const dias = filtroTokensPeriodo ? parseInt(filtroTokensPeriodo, 10) : undefined;
      const [resumo, usuarios, organizacoes] = await Promise.all([
        obterResumoConsumoTokens({ dias }),
        listarConsumoUsuarios({ dias, limite: 50 }),
        listarConsumoOrganizacoes({ dias, limite: 50 }),
      ]);
      setResumoTokens(resumo);
      setConsumoUsuarios(usuarios);
      setConsumoOrganizacoes(organizacoes);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [filtroTokensPeriodo, mostrarNotificacao]);

  const handleSincronizarTokensOrganizacoes = useCallback(async () => {
    try {
      setSincronizandoTokens(true);
      const resultado = await sincronizarOrganizacoesConsumo();
      
      if (resultado.sucesso) {
        mostrarNotificacao('sucesso', resultado.mensagem);
        // Recarregar os dados de consumo para refletir as mudanças
        await carregarConsumoTokens();
      } else {
        mostrarNotificacao('erro', resultado.mensagem || 'Erro ao sincronizar organizações');
      }
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setSincronizandoTokens(false);
    }
  }, [mostrarNotificacao, carregarConsumoTokens]);

  const carregarOrganizacoes = useCallback(async () => {
    try {
      setCarregando(true);
      const dados = await listarOrganizacoesAdmin({ incluir_inativas: incluirOrgsInativas });
      setOrganizacoes(dados);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  }, [incluirOrgsInativas, mostrarNotificacao]);

  const carregarMembrosOrganizacao = useCallback(async (organizacaoId: string) => {
    try {
      setCarregandoMembros(true);
      const membros = await listarMembrosOrganizacaoAdmin(organizacaoId);
      setMembrosOrgExpandida(membros);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregandoMembros(false);
    }
  }, [mostrarNotificacao]);

  // ==================== AÇÕES ====================

  const handleAlterarRole = async (usuarioId: string, novoRole: 'admin' | 'user') => {
    try {
      await alterarRoleUsuario(usuarioId, novoRole);
      mostrarNotificacao('sucesso', `Role alterado para ${novoRole} com sucesso!`);
      carregarUsuarios();
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleAtivarDesativar = async (usuarioId: string, ativo: boolean) => {
    try {
      await ativarDesativarUsuario(usuarioId, ativo);
      mostrarNotificacao('sucesso', `Usuário ${ativo ? 'ativado' : 'desativado'} com sucesso!`);
      carregarUsuarios();
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleResetarSenha = async () => {
    if (!novaSenha || novaSenha.length < 8) {
      mostrarNotificacao('erro', 'A senha deve ter pelo menos 8 caracteres.');
      return;
    }
    try {
      await resetarSenhaUsuario(modalResetSenha.usuarioId, novaSenha);
      mostrarNotificacao('sucesso', 'Senha resetada com sucesso!');
      setModalResetSenha({ aberto: false, usuarioId: '', usuarioNome: '' });
      setNovaSenha('');
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleVerProcesso = async (processoId: string) => {
    try {
      setCarregando(true);
      const detalhe = await obterProcessoDetalhe(processoId);
      setProcessoDetalhe(detalhe);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    } finally {
      setCarregando(false);
    }
  };

  const handleExcluirProcesso = async (processoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este processo e todos seus documentos?')) {
      return;
    }
    try {
      await excluirProcesso(processoId);
      mostrarNotificacao('sucesso', 'Processo excluído com sucesso!');
      carregarProcessos();
      setProcessoDetalhe(null);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleDownloadArquivo = async (arquivo: ArquivoInfo) => {
    try {
      const blob = await downloadArquivo(filtroArquivoDiretorio, arquivo.caminho);
      iniciarDownloadBlob(blob, arquivo.nome);
      mostrarNotificacao('sucesso', 'Download iniciado!');
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleExcluirArquivo = async (arquivo: ArquivoInfo) => {
    if (!confirm(`Tem certeza que deseja excluir o arquivo "${arquivo.nome}"?`)) {
      return;
    }
    try {
      await excluirArquivo(filtroArquivoDiretorio, arquivo.caminho);
      mostrarNotificacao('sucesso', 'Arquivo excluído com sucesso!');
      carregarArquivos();
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  // ==================== AÇÕES DE ORGANIZAÇÕES ====================

  const handleExpandirOrganizacao = async (organizacaoId: string) => {
    if (orgExpandida === organizacaoId) {
      setOrgExpandida(null);
      setMembrosOrgExpandida([]);
    } else {
      setOrgExpandida(organizacaoId);
      await carregarMembrosOrganizacao(organizacaoId);
    }
  };

  const handleCriarOrganizacao = async () => {
    if (!novaOrgNome.trim() || !novaOrgOwnerId.trim()) {
      mostrarNotificacao('erro', 'Nome e ID do owner são obrigatórios.');
      return;
    }
    try {
      await criarOrganizacaoAdmin({
        nome: novaOrgNome,
        descricao: novaOrgDescricao || undefined,
        owner_usuario_id: novaOrgOwnerId,
      });
      mostrarNotificacao('sucesso', 'Organização criada com sucesso!');
      setModalNovaOrg(false);
      setNovaOrgNome('');
      setNovaOrgDescricao('');
      setNovaOrgOwnerId('');
      carregarOrganizacoes();
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleAtivarDesativarOrganizacao = async (org: OrganizacaoAdmin) => {
    try {
      await atualizarOrganizacaoAdmin(org.id, { ativa: !org.ativa });
      mostrarNotificacao('sucesso', `Organização ${!org.ativa ? 'ativada' : 'desativada'} com sucesso!`);
      carregarOrganizacoes();
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleExcluirOrganizacao = async (org: OrganizacaoAdmin, permanente = false) => {
    const msg = permanente
      ? `ATENÇÃO: Isso vai EXCLUIR PERMANENTEMENTE a organização "${org.nome}" e todos os seus membros. Esta ação NÃO pode ser desfeita. Continuar?`
      : `Deseja desativar a organização "${org.nome}"?`;
    
    if (!confirm(msg)) return;
    
    try {
      await excluirOrganizacaoAdmin(org.id, permanente);
      mostrarNotificacao('sucesso', `Organização ${permanente ? 'excluída permanentemente' : 'desativada'} com sucesso!`);
      carregarOrganizacoes();
      if (orgExpandida === org.id) {
        setOrgExpandida(null);
        setMembrosOrgExpandida([]);
      }
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleAdicionarMembro = async () => {
    if (!novoMembroId.trim()) {
      mostrarNotificacao('erro', 'ID do usuário é obrigatório.');
      return;
    }
    try {
      await adicionarMembroOrganizacaoAdmin(modalAdicionarMembro.organizacaoId, {
        usuario_id: novoMembroId,
        role: novoMembroRole,
      });
      mostrarNotificacao('sucesso', 'Membro adicionado com sucesso!');
      setModalAdicionarMembro({ aberto: false, organizacaoId: '', organizacaoNome: '' });
      setNovoMembroId('');
      setNovoMembroRole('member');
      if (orgExpandida) {
        await carregarMembrosOrganizacao(orgExpandida);
      }
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleAlterarRoleMembro = async (organizacaoId: string, usuarioId: string, novoRole: 'owner' | 'admin' | 'member') => {
    try {
      await alterarRoleMembroAdmin(organizacaoId, usuarioId, novoRole);
      mostrarNotificacao('sucesso', `Role alterado para ${novoRole} com sucesso!`);
      await carregarMembrosOrganizacao(organizacaoId);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  const handleRemoverMembro = async (organizacaoId: string, usuarioId: string, nomeUsuario: string) => {
    if (!confirm(`Remover "${nomeUsuario}" desta organização?`)) return;
    
    try {
      await removerMembroOrganizacaoAdmin(organizacaoId, usuarioId);
      mostrarNotificacao('sucesso', 'Membro removido com sucesso!');
      await carregarMembrosOrganizacao(organizacaoId);
    } catch (err) {
      mostrarNotificacao('erro', (err as Error).message);
    }
  };

  // ==================== EFEITOS ====================

  useEffect(() => {
    carregarEstatisticas();
  }, [carregarEstatisticas]);

  useEffect(() => {
    if (tabAtiva === 'usuarios') carregarUsuarios();
    else if (tabAtiva === 'processos') carregarProcessos();
    else if (tabAtiva === 'logs') carregarLogs();
    else if (tabAtiva === 'arquivos') carregarArquivos();
    else if (tabAtiva === 'metricas') carregarMetricas();
    else if (tabAtiva === 'tokens') carregarConsumoTokens();
    else if (tabAtiva === 'organizacoes') carregarOrganizacoes();
  }, [tabAtiva, carregarUsuarios, carregarProcessos, carregarLogs, carregarArquivos, carregarMetricas, carregarConsumoTokens, carregarOrganizacoes]);

  // ==================== RENDERIZAÇÃO ====================

  const tabs: { id: TabAtiva; label: string; icone: React.ElementType }[] = [
    { id: 'visao-geral', label: 'Visão Geral', icone: BarChart3 },
    { id: 'usuarios', label: 'Usuários', icone: Users },
    { id: 'organizacoes', label: 'Organizações', icone: Building2 },
    { id: 'processos', label: 'Processos', icone: FileText },
    { id: 'logs', label: 'Logs', icone: Activity },
    { id: 'arquivos', label: 'Arquivos', icone: FolderOpen },
    { id: 'metricas', label: 'Métricas', icone: Database },
    { id: 'tokens', label: 'Consumo de Tokens', icone: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="size-8 text-primary-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Painel de Administração</h1>
              <p className="text-sm text-gray-500">Gerencie usuários, processos, logs e métricas do sistema</p>
            </div>
          </div>
          <button
            onClick={() => {
              if (tabAtiva === 'visao-geral') carregarEstatisticas();
              else if (tabAtiva === 'usuarios') carregarUsuarios();
              else if (tabAtiva === 'processos') carregarProcessos();
              else if (tabAtiva === 'logs') carregarLogs();
              else if (tabAtiva === 'arquivos') carregarArquivos();
              else if (tabAtiva === 'metricas') carregarMetricas();
              else if (tabAtiva === 'tokens') carregarConsumoTokens();
              else if (tabAtiva === 'organizacoes') carregarOrganizacoes();
            }}
            disabled={carregando}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-white hover:bg-primary-700 disabled:opacity-50"
          >
            <RefreshCw className={`size-4 ${carregando ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </header>

      {/* Notificação */}
      <div className="mx-auto max-w-7xl px-6 pt-4">
        {notificacao && (
          <NotificacaoBanner notificacao={notificacao} onFechar={() => setNotificacao(null)} />
        )}
      </div>

      {/* Tabs */}
      <nav className="border-b bg-white px-6">
        <div className="mx-auto flex max-w-7xl gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setTabAtiva(tab.id)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                tabAtiva === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <tab.icone className="size-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Conteúdo */}
      <main className="mx-auto max-w-7xl p-6">
        {carregando && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
            <div className="rounded-lg bg-white p-6 shadow-xl">
              <Loader2 className="size-8 animate-spin text-primary-600" />
            </div>
          </div>
        )}

        {/* Tab: Visão Geral */}
        {tabAtiva === 'visao-geral' && estatisticas && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <CardEstatistica
                titulo="Total de Usuários"
                valor={estatisticas.total_usuarios}
                icone={Users}
                cor="bg-blue-500"
              />
              <CardEstatistica
                titulo="Usuários Ativos"
                valor={estatisticas.usuarios_ativos}
                icone={CheckCircle}
                cor="bg-green-500"
              />
              <CardEstatistica
                titulo="Total de Processos"
                valor={estatisticas.total_processos}
                icone={FileText}
                cor="bg-purple-500"
              />
              <CardEstatistica
                titulo="Total de Arquivos"
                valor={estatisticas.total_arquivos}
                icone={HardDrive}
                cor="bg-orange-500"
              />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Uso de Disco */}
              <div className="rounded-lg bg-white p-6 shadow-md">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <HardDrive className="size-5 text-gray-500" />
                  Uso de Disco
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Espaço Total Utilizado</span>
                    <span className="font-medium">{estatisticas.espaco_utilizado_mb?.toFixed(2) ?? '0.00'} MB</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Arquivos</span>
                    <span className="font-medium">{estatisticas.total_arquivos ?? 0}</span>
                  </div>
                </div>
              </div>

              {/* Processos por Status */}
              <div className="rounded-lg bg-white p-6 shadow-md">
                <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Activity className="size-5 text-gray-500" />
                  Processos por Status
                </h3>
                <div className="space-y-4">
                  {Object.entries(estatisticas.processos_por_status).map(([status, count]) => (
                    <div key={status} className="flex items-center justify-between">
                      <span className="capitalize text-gray-600">{status.replace(/_/g, ' ')}</span>
                      <Badge variante={status === 'concluido' ? 'success' : status === 'erro' ? 'error' : 'default'}>
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Usuários */}
        {tabAtiva === 'usuarios' && (
          <div className="space-y-4">
            {/* Filtros */}
            <div className="flex flex-wrap gap-4 rounded-lg bg-white p-4 shadow-md">
              <div className="flex items-center gap-2">
                <Filter className="size-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filtros:</span>
              </div>
              <select
                value={filtroUsuarioRole}
                onChange={(e) => setFiltroUsuarioRole(e.target.value)}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                <option value="">Todos os Roles</option>
                <option value="admin">Admin</option>
                <option value="user">Usuário</option>
              </select>
              <select
                value={filtroUsuarioAtivo}
                onChange={(e) => setFiltroUsuarioAtivo(e.target.value)}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                <option value="">Todos os Status</option>
                <option value="true">Ativos</option>
                <option value="false">Inativos</option>
              </select>
              <button
                onClick={carregarUsuarios}
                className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium hover:bg-gray-200"
              >
                Aplicar
              </button>
            </div>

            {/* Tabela */}
            <div className="overflow-hidden rounded-lg bg-white shadow-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex size-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                            {usuario.nome.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{usuario.nome}</div>
                            <div className="text-sm text-gray-500">{usuario.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <Badge variante={usuario.role === 'admin' ? 'info' : 'default'}>
                          {usuario.role === 'admin' ? (
                            <span className="flex items-center gap-1">
                              <Shield className="size-3" /> Admin
                            </span>
                          ) : (
                            'Usuário'
                          )}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <Badge variante={usuario.ativo ? 'success' : 'error'}>
                          {usuario.ativo ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {new Date(usuario.data_criacao).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleAlterarRole(usuario.id, usuario.role === 'admin' ? 'user' : 'admin')}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            title={usuario.role === 'admin' ? 'Remover Admin' : 'Tornar Admin'}
                          >
                            {usuario.role === 'admin' ? <ShieldOff className="size-4" /> : <Shield className="size-4" />}
                          </button>
                          <button
                            onClick={() => handleAtivarDesativar(usuario.id, !usuario.ativo)}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            title={usuario.ativo ? 'Desativar' : 'Ativar'}
                          >
                            {usuario.ativo ? <XCircle className="size-4" /> : <CheckCircle className="size-4" />}
                          </button>
                          <button
                            onClick={() => setModalResetSenha({ aberto: true, usuarioId: usuario.id, usuarioNome: usuario.nome })}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            title="Resetar Senha"
                          >
                            <Key className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab: Processos */}
        {tabAtiva === 'processos' && (
          <div className="space-y-4">
            {processoDetalhe ? (
              <div className="space-y-4">
                <button
                  onClick={() => setProcessoDetalhe(null)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  <ChevronRight className="size-4 rotate-180" />
                  Voltar à lista
                </button>
                <div className="rounded-lg bg-white p-6 shadow-md">
                  <h3 className="mb-4 text-lg font-semibold">Detalhes do Processo</h3>
                  <pre className="overflow-auto rounded bg-gray-100 p-4 text-sm">
                    {JSON.stringify(processoDetalhe.processo, null, 2)}
                  </pre>
                  <h4 className="mb-2 mt-6 font-medium">Documentos ({processoDetalhe.documentos.length})</h4>
                  <pre className="overflow-auto rounded bg-gray-100 p-4 text-sm">
                    {JSON.stringify(processoDetalhe.documentos, null, 2)}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg bg-white shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Processo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Usuário
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Documentos
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Criado em
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {processos.map((processo) => (
                      <tr key={processo.processo_id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {processo.nome || processo.processo_id.slice(0, 8)}
                          </div>
                          <div className="text-xs text-gray-500">{processo.estado_wizard || 'N/A'}</div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="text-sm text-gray-900">{processo.usuario_email || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{processo.usuario_id?.slice(0, 8) || ''}</div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <Badge
                            variante={
                              processo.status_processamento === 'concluido'
                                ? 'success'
                                : processo.status_processamento === 'erro'
                                ? 'error'
                                : processo.status_processamento === 'processando'
                                ? 'warning'
                                : 'default'
                            }
                          >
                            {processo.status_processamento}
                          </Badge>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {processo.total_documentos}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {new Date(processo.criado_em).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleVerProcesso(processo.processo_id)}
                              className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                              title="Ver Detalhes"
                            >
                              <Eye className="size-4" />
                            </button>
                            <button
                              onClick={() => handleExcluirProcesso(processo.processo_id)}
                              className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                              title="Excluir Processo"
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab: Logs */}
        {tabAtiva === 'logs' && (
          <div className="space-y-4">
            {/* Filtros */}
            <div className="flex flex-wrap gap-4 rounded-lg bg-white p-4 shadow-md">
              <div className="flex items-center gap-2">
                <Filter className="size-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filtros:</span>
              </div>
              <select
                value={filtroLogNivel}
                onChange={(e) => setFiltroLogNivel(e.target.value)}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                <option value="">Todos os Níveis</option>
                <option value="DEBUG">DEBUG</option>
                <option value="INFO">INFO</option>
                <option value="WARNING">WARNING</option>
                <option value="ERROR">ERROR</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
              <button
                onClick={carregarLogs}
                className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium hover:bg-gray-200"
              >
                Aplicar
              </button>
            </div>

            {/* Lista de Logs */}
            <div className="space-y-2">
              {logs.length === 0 ? (
                <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow-md">
                  Nenhum log encontrado.
                </div>
              ) : (
                logs.map((log) => (
                  <div
                    key={log._id}
                    className={`rounded-lg bg-white p-4 shadow-md ${
                      log.level === 'ERROR' || log.level === 'CRITICAL'
                        ? 'border-l-4 border-red-500'
                        : log.level === 'WARNING'
                        ? 'border-l-4 border-yellow-500'
                        : 'border-l-4 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge
                            variante={
                              log.level === 'ERROR' || log.level === 'CRITICAL'
                                ? 'error'
                                : log.level === 'WARNING'
                                ? 'warning'
                                : log.level === 'INFO'
                                ? 'info'
                                : 'default'
                            }
                          >
                            {log.level}
                          </Badge>
                          <span className="text-xs text-gray-500">{log.module}</span>
                        </div>
                        <p className="mt-2 text-sm text-gray-900">{log.message}</p>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(log.timestamp).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Tab: Arquivos */}
        {tabAtiva === 'arquivos' && (
          <div className="space-y-4">
            {/* Filtros */}
            <div className="flex flex-wrap gap-4 rounded-lg bg-white p-4 shadow-md">
              <div className="flex items-center gap-2">
                <FolderOpen className="size-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Diretório:</span>
              </div>
              <select
                value={filtroArquivoDiretorio}
                onChange={(e) => setFiltroArquivoDiretorio(e.target.value)}
                className="rounded-md border px-3 py-1.5 text-sm"
              >
                <option value="uploads">Uploads</option>
                <option value="logs">Logs</option>
              </select>
              <button
                onClick={carregarArquivos}
                className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium hover:bg-gray-200"
              >
                Atualizar
              </button>
            </div>

            {/* Lista de Arquivos */}
            <div className="overflow-hidden rounded-lg bg-white shadow-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Nome
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Tamanho
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Modificado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {arquivos.map((arquivo) => (
                    <tr key={arquivo.caminho} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FileText className="size-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{arquivo.nome}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {arquivo.tipo}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {(arquivo.tamanho_bytes / 1024).toFixed(2)} KB
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {new Date(arquivo.data_modificacao).toLocaleString('pt-BR')}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleDownloadArquivo(arquivo)}
                            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            title="Download"
                          >
                            <Download className="size-4" />
                          </button>
                          <button
                            onClick={() => handleExcluirArquivo(arquivo)}
                            className="rounded p-1 text-red-500 hover:bg-red-50 hover:text-red-700"
                            title="Excluir"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab: Métricas */}
        {tabAtiva === 'metricas' && (
          <div className="space-y-6">
            {(!metricas || !metricas.sistema) && (
              <div className="rounded-lg bg-yellow-50 p-4 text-yellow-800">
                <p className="font-medium">Métricas de sistema não disponíveis</p>
                <p className="text-sm">O servidor pode não estar reportando métricas de sistema no momento.</p>
              </div>
            )}
            {metricas && metricas.sistema && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {/* Sistema */}
                <div className="rounded-lg bg-white p-6 shadow-md">
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                    <HardDrive className="size-5 text-blue-500" />
                    Sistema
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">CPU</span>
                        <span className="font-medium">{metricas.sistema.cpu_percent?.toFixed(1) ?? 0}%</span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-blue-500"
                          style={{ width: `${metricas.sistema.cpu_percent ?? 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Memória</span>
                        <span className="font-medium">{metricas.sistema.memoria_percent?.toFixed(1) ?? 0}%</span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-green-500"
                          style={{ width: `${metricas.sistema.memoria_percent ?? 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Disco</span>
                        <span className="font-medium">{metricas.sistema.disco_percent?.toFixed(1) ?? 0}%</span>
                      </div>
                      <div className="mt-1 h-2 w-full rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-purple-500"
                          style={{ width: `${metricas.sistema.disco_percent ?? 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aplicação */}
                <div className="rounded-lg bg-white p-6 shadow-md">
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                    <Activity className="size-5 text-green-500" />
                    Aplicação
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Requisições Total</span>
                      <span className="font-medium">{metricas.aplicacao?.requisicoes_total ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Erros</span>
                      <span className="font-medium text-red-600">{metricas.aplicacao?.requisicoes_erro ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Taxa de Erro</span>
                      <span className="font-medium">{metricas.aplicacao?.taxa_erro?.toFixed(2) ?? 0}%</span>
                    </div>
                  </div>
                </div>

                {/* Uploads & Análises */}
                <div className="rounded-lg bg-white p-6 shadow-md">
                  <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                    <Users className="size-5 text-purple-500" />
                    Processamento
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Uploads Total</span>
                      <span className="font-medium">{metricas.uploads?.total ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Análises Total</span>
                      <span className="font-medium">{metricas.analises?.total ?? 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Análises Sucesso</span>
                      <span className="font-medium text-green-600">{metricas.analises?.sucesso ?? 0}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Métricas Prometheus Raw */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                <Database className="size-5 text-orange-500" />
                Métricas Prometheus (Raw)
              </h3>
              <pre className="max-h-96 overflow-auto rounded bg-gray-900 p-4 text-xs text-green-400">
                {metricasPrometheus || 'Carregando métricas...'}
              </pre>
            </div>
          </div>
        )}

        {/* Tab: Consumo de Tokens */}
        {tabAtiva === 'tokens' && (
          <div className="space-y-6">
            {/* Filtros */}
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Período:</label>
                <select
                  value={filtroTokensPeriodo}
                  onChange={(e) => setFiltroTokensPeriodo(e.target.value)}
                  className="rounded-md border px-3 py-1.5 text-sm"
                >
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="365">Último ano</option>
                  <option value="">Todo o período</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Usuário:</label>
                <input
                  type="text"
                  value={filtroTokensUsuario}
                  onChange={(e) => setFiltroTokensUsuario(e.target.value)}
                  placeholder="Filtrar por nome ou email..."
                  className="w-48 rounded-md border px-3 py-1.5 text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Organização:</label>
                <input
                  type="text"
                  value={filtroTokensOrganizacao}
                  onChange={(e) => setFiltroTokensOrganizacao(e.target.value)}
                  placeholder="Filtrar por nome..."
                  className="w-48 rounded-md border px-3 py-1.5 text-sm"
                />
              </div>
              <button
                onClick={carregarConsumoTokens}
                className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium hover:bg-gray-200"
              >
                Aplicar
              </button>
              {(filtroTokensUsuario || filtroTokensOrganizacao) && (
                <button
                  onClick={() => {
                    setFiltroTokensUsuario('');
                    setFiltroTokensOrganizacao('');
                  }}
                  className="rounded-md px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100"
                >
                  Limpar filtros
                </button>
              )}
              {/* Botão de sincronização de organizações */}
              <button
                onClick={handleSincronizarTokensOrganizacoes}
                disabled={sincronizandoTokens}
                className="ml-auto rounded-md bg-orange-100 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-200 disabled:cursor-not-allowed disabled:opacity-50"
                title="Sincroniza os registros de consumo antigos que não têm organização associada"
              >
                {sincronizandoTokens ? (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="size-4 animate-spin" />
                    Sincronizando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="size-4" />
                    Sincronizar Organizações
                  </span>
                )}
              </button>
            </div>

            {/* Resumo Geral */}
            {resumoTokens && (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <CardEstatistica
                  titulo="Tokens Consumidos"
                  valor={formatarTokens(resumoTokens.total_tokens_global)}
                  icone={BarChart3}
                  cor="bg-blue-500"
                />
                <CardEstatistica
                  titulo="Custo Total (USD)"
                  valor={formatarCustoUSD(resumoTokens.custo_total_global_usd)}
                  icone={Database}
                  cor="bg-green-500"
                />
                <CardEstatistica
                  titulo="Total de Chamadas"
                  valor={resumoTokens.total_chamadas_global}
                  icone={Activity}
                  cor="bg-purple-500"
                />
                <CardEstatistica
                  titulo="Usuários Ativos"
                  valor={resumoTokens.total_usuarios_com_consumo}
                  icone={Users}
                  cor="bg-orange-500"
                />
              </div>
            )}

            {/* Consumo por Modelo */}
            {resumoTokens && Object.keys(resumoTokens.consumo_por_modelo).length > 0 && (
              <div className="rounded-lg bg-white p-6 shadow-md">
                <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                  <Database className="size-5 text-blue-500" />
                  Consumo por Modelo LLM
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Modelo
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Tokens
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Custo (USD)
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Chamadas
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {Object.entries(resumoTokens.consumo_por_modelo).map(([modelo, dados]) => (
                        <tr key={modelo}>
                          <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">
                            {modelo}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-gray-600">
                            {formatarTokens(dados.total_tokens)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-gray-600">
                            {formatarCustoUSD(dados.custo_total_usd)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-gray-600">
                            {dados.total_chamadas}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Usuários */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                <Users className="size-5 text-purple-500" />
                Top Usuários por Consumo
              </h3>
              {consumoUsuarios.length === 0 ? (
                <p className="text-center text-gray-500">Nenhum consumo registrado no período.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Usuário
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Organização
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Tokens
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Custo (USD)
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Chamadas
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Última Chamada
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {consumoUsuarios
                        .filter((consumo) => {
                          const termoUsuario = filtroTokensUsuario.toLowerCase();
                          const termoOrg = filtroTokensOrganizacao.toLowerCase();
                          const matchUsuario = !termoUsuario || 
                            (consumo.nome?.toLowerCase().includes(termoUsuario) || 
                             consumo.email?.toLowerCase().includes(termoUsuario));
                          const matchOrg = !termoOrg || 
                            consumo.nome_organizacao?.toLowerCase().includes(termoOrg);
                          return matchUsuario && matchOrg;
                        })
                        .map((consumo) => (
                        <tr key={consumo.usuario_id}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{consumo.nome || 'N/A'}</p>
                              <p className="text-sm text-gray-500">{consumo.email || consumo.usuario_id}</p>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-gray-600">
                            {consumo.nome_organizacao || '-'}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right font-medium text-blue-600">
                            {formatarTokens(consumo.total_tokens)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-green-600">
                            {formatarCustoUSD(consumo.custo_total_usd)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-gray-600">
                            {consumo.total_chamadas}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-sm text-gray-500">
                            {consumo.ultima_chamada
                              ? new Date(consumo.ultima_chamada).toLocaleDateString('pt-BR')
                              : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Top Organizações */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <h3 className="mb-4 flex items-center gap-2 font-semibold text-gray-900">
                <FolderOpen className="size-5 text-orange-500" />
                Consumo por Organização
              </h3>
              {consumoOrganizacoes.length === 0 ? (
                <p className="text-center text-gray-500">Nenhum consumo de organizações registrado no período.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          Organização
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Tokens
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Custo (USD)
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Chamadas
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                          Membros Ativos
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {consumoOrganizacoes
                        .filter((consumo) => {
                          const termoOrg = filtroTokensOrganizacao.toLowerCase();
                          return !termoOrg || 
                            consumo.nome_organizacao?.toLowerCase().includes(termoOrg) ||
                            consumo.organizacao_id.toLowerCase().includes(termoOrg);
                        })
                        .map((consumo) => (
                        <tr key={consumo.organizacao_id}>
                          <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">
                            {consumo.nome_organizacao || consumo.organizacao_id}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right font-medium text-blue-600">
                            {formatarTokens(consumo.total_tokens)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-green-600">
                            {formatarCustoUSD(consumo.custo_total_usd)}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-gray-600">
                            {consumo.total_chamadas}
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-right text-gray-600">
                            {consumo.membros_com_consumo}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Organizações */}
        {tabAtiva === 'organizacoes' && (
          <div className="space-y-4">
            {/* Header e Filtros */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-white p-4 shadow-md">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="size-4 text-gray-500" />
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={incluirOrgsInativas}
                      onChange={(e) => setIncluirOrgsInativas(e.target.checked)}
                      className="rounded"
                    />
                    Incluir inativas
                  </label>
                </div>
                <button
                  onClick={carregarOrganizacoes}
                  className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium hover:bg-gray-200"
                >
                  Aplicar
                </button>
              </div>
              <button
                onClick={() => setModalNovaOrg(true)}
                className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700"
              >
                <Plus className="size-4" />
                Nova Organização
              </button>
            </div>

            {/* Lista de Organizações */}
            <div className="space-y-3">
              {organizacoes.length === 0 ? (
                <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow-md">
                  Nenhuma organização encontrada.
                </div>
              ) : (
                organizacoes.map((org) => (
                  <div key={org.id} className="rounded-lg bg-white shadow-md">
                    {/* Header da Organização */}
                    <div
                      className="flex cursor-pointer items-center justify-between p-4 hover:bg-gray-50"
                      onClick={() => handleExpandirOrganizacao(org.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`flex size-12 items-center justify-center rounded-lg ${org.ativa ? 'bg-primary-100 text-primary-600' : 'bg-gray-200 text-gray-500'}`}>
                          <Building2 className="size-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{org.nome}</h3>
                            <Badge variante={org.ativa ? 'success' : 'error'}>
                              {org.ativa ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500">
                            {org.slug} • {org.total_membros} membro{org.total_membros !== 1 ? 's' : ''}
                            {org.owner_nome && ` • Owner: ${org.owner_nome}`}
                          </p>
                          {org.descricao && (
                            <p className="mt-1 text-sm text-gray-400">{org.descricao}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAtivarDesativarOrganizacao(org);
                          }}
                          className={`rounded p-1.5 ${org.ativa ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}
                          title={org.ativa ? 'Desativar' : 'Ativar'}
                        >
                          {org.ativa ? <XCircle className="size-4" /> : <CheckCircle className="size-4" />}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExcluirOrganizacao(org, false);
                          }}
                          className="rounded p-1.5 text-red-600 hover:bg-red-50"
                          title="Excluir"
                        >
                          <Trash2 className="size-4" />
                        </button>
                        {orgExpandida === org.id ? (
                          <ChevronUp className="size-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="size-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Membros Expandidos */}
                    {orgExpandida === org.id && (
                      <div className="border-t bg-gray-50 p-4">
                        <div className="mb-3 flex items-center justify-between">
                          <h4 className="flex items-center gap-2 font-medium text-gray-700">
                            <Users className="size-4" />
                            Membros da Organização
                          </h4>
                          <button
                            onClick={() => setModalAdicionarMembro({
                              aberto: true,
                              organizacaoId: org.id,
                              organizacaoNome: org.nome,
                            })}
                            className="flex items-center gap-1 rounded-md bg-primary-100 px-3 py-1.5 text-sm text-primary-700 hover:bg-primary-200"
                          >
                            <UserPlus className="size-4" />
                            Adicionar Membro
                          </button>
                        </div>

                        {carregandoMembros ? (
                          <div className="flex justify-center py-4">
                            <Loader2 className="size-6 animate-spin text-primary-600" />
                          </div>
                        ) : membrosOrgExpandida.length === 0 ? (
                          <p className="text-center text-gray-500">Nenhum membro encontrado.</p>
                        ) : (
                          <div className="overflow-hidden rounded-lg border bg-white">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Usuário
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Role
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                                    Desde
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                                    Ações
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {membrosOrgExpandida.map((membro) => (
                                  <tr key={membro.id} className={!membro.ativo ? 'bg-gray-50 opacity-60' : ''}>
                                    <td className="whitespace-nowrap px-4 py-3">
                                      <div className="flex items-center gap-3">
                                        <div className="flex size-8 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                                          {membro.nome_usuario.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-gray-900">{membro.nome_usuario}</p>
                                          <p className="text-xs text-gray-500">{membro.email_usuario}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-3">
                                      <Badge
                                        variante={
                                          membro.role === 'owner' ? 'warning' :
                                          membro.role === 'admin' ? 'info' : 'default'
                                        }
                                      >
                                        {membro.role === 'owner' && <Crown className="mr-1 size-3" />}
                                        {membro.role === 'owner' ? 'Proprietário' :
                                         membro.role === 'admin' ? 'Admin' : 'Membro'}
                                      </Badge>
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                                      {new Date(membro.data_entrada).toLocaleDateString('pt-BR')}
                                    </td>
                                    <td className="whitespace-nowrap px-4 py-3 text-right">
                                      <div className="flex justify-end gap-1">
                                        {/* Alterar Role */}
                                        {membro.role !== 'owner' && (
                                          <select
                                            value={membro.role}
                                            onChange={(e) => handleAlterarRoleMembro(
                                              org.id,
                                              membro.usuario_id,
                                              e.target.value as 'owner' | 'admin' | 'member'
                                            )}
                                            className="rounded border px-2 py-1 text-xs"
                                          >
                                            <option value="member">Membro</option>
                                            <option value="admin">Admin</option>
                                            <option value="owner">Proprietário</option>
                                          </select>
                                        )}
                                        {/* Remover Membro */}
                                        {membro.role !== 'owner' && (
                                          <button
                                            onClick={() => handleRemoverMembro(org.id, membro.usuario_id, membro.nome_usuario)}
                                            className="rounded p-1 text-red-500 hover:bg-red-50"
                                            title="Remover da organização"
                                          >
                                            <UserMinus className="size-4" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Info adicional da org */}
                        <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-400">
                          <span>ID: {org.id}</span>
                          <span>Criada em: {new Date(org.data_criacao).toLocaleDateString('pt-BR')}</span>
                          <span>Atualizada: {new Date(org.data_atualizacao).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Modal Reset Senha */}
      {modalResetSenha.aberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 text-lg font-semibold">Resetar Senha</h3>
            <p className="mb-4 text-sm text-gray-600">
              Digite a nova senha para <strong>{modalResetSenha.usuarioNome}</strong>:
            </p>
            <input
              type="password"
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              placeholder="Nova senha (mínimo 8 caracteres)"
              className="mb-4 w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setModalResetSenha({ aberto: false, usuarioId: '', usuarioNome: '' });
                  setNovaSenha('');
                }}
                className="rounded-md px-4 py-2 text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleResetarSenha}
                className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Nova Organização */}
      {modalNovaOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
              <Building2 className="size-5 text-primary-600" />
              Nova Organização
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Nome da Organização *
                </label>
                <input
                  type="text"
                  value={novaOrgNome}
                  onChange={(e) => setNovaOrgNome(e.target.value)}
                  placeholder="Ex: Escritório Silva Advogados"
                  className="w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Descrição
                </label>
                <textarea
                  value={novaOrgDescricao}
                  onChange={(e) => setNovaOrgDescricao(e.target.value)}
                  placeholder="Descrição opcional da organização"
                  rows={2}
                  className="w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  ID do Proprietário (Owner) *
                </label>
                <input
                  type="text"
                  value={novaOrgOwnerId}
                  onChange={(e) => setNovaOrgOwnerId(e.target.value)}
                  placeholder="UUID do usuário que será o owner"
                  className="w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Copie o ID do usuário da lista de usuários
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setModalNovaOrg(false);
                  setNovaOrgNome('');
                  setNovaOrgDescricao('');
                  setNovaOrgOwnerId('');
                }}
                className="rounded-md px-4 py-2 text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleCriarOrganizacao}
                className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
              >
                Criar Organização
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Membro */}
      {modalAdicionarMembro.aberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
              <UserPlus className="size-5 text-primary-600" />
              Adicionar Membro
            </h3>
            <p className="mb-4 text-sm text-gray-600">
              Adicionar membro à organização <strong>{modalAdicionarMembro.organizacaoNome}</strong>:
            </p>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  ID do Usuário *
                </label>
                <input
                  type="text"
                  value={novoMembroId}
                  onChange={(e) => setNovoMembroId(e.target.value)}
                  placeholder="UUID do usuário a ser adicionado"
                  className="w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Copie o ID do usuário da lista de usuários
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  value={novoMembroRole}
                  onChange={(e) => setNovoMembroRole(e.target.value as 'admin' | 'member')}
                  className="w-full rounded-md border px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="member">Membro</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setModalAdicionarMembro({ aberto: false, organizacaoId: '', organizacaoNome: '' });
                  setNovoMembroId('');
                  setNovoMembroRole('member');
                }}
                className="rounded-md px-4 py-2 text-gray-600 hover:bg-gray-100"
              >
                Cancelar
              </button>
              <button
                onClick={handleAdicionarMembro}
                className="rounded-md bg-primary-600 px-4 py-2 text-white hover:bg-primary-700"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaginaAdmin;
